import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../api/client';
import { billNumberOf, posApi, saleTotalOf, type PosSale } from '../api/pos';
import { money } from '../format';

/**
 * Asking an admin to cancel a bill.
 *
 * The counter cannot cancel a bill — that stays an admin's decision, because
 * it reverses a paid sale and puts stock back. What the counter can do is ask,
 * with the reason, while the customer is still standing there. The shop's rule
 * is that the money goes back straight away, so this says so before anything
 * is sent and asks staff to confirm they have handed it over.
 */
export default function RequestCancel({
  sale,
  onCancel,
  onSent,
}: {
  sale: PosSale;
  onCancel: () => void;
  onSent: (updated: PosSale) => void;
}) {
  const [reason, setReason] = useState('');
  const [handedBack, setHandedBack] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const total = saleTotalOf(sale);

  useEffect(() => {
    reasonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, sending]);

  async function send() {
    if (sending) return;
    if (!reason.trim()) {
      setError('Say why this bill should be cancelled.');
      reasonRef.current?.focus();
      return;
    }
    if (!handedBack) {
      setError(`Tick the box once ${money(total)} has been handed back to the customer.`);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const updated = await posApi.requestCancel(sale.id, reason.trim());
      onSent(updated);
    } catch (err) {
      setError(errorMessage(err, 'The request could not be sent.'));
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        // Stops here, like the edit dialog: the bill view's own backdrop is
        // underneath and would close too.
        e.stopPropagation();
        if (!sending) onCancel();
      }}
    >
      <div
        className="modal modal-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-cancel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="request-cancel-title" className="confirm-title">
          Ask an admin to cancel {billNumberOf(sale)}
        </h2>
        <p className="muted small">
          Only an admin can cancel a bill. They will be emailed straight away, and the bill
          shows as <strong>Cancel requested</strong> until they decide.
        </p>

        <p className="cancel-req-money">
          Hand <strong>{money(total)}</strong> back to the customer now.
        </p>

        <label className="field-label" htmlFor="cancel-reason">
          Why should this bill be cancelled?
        </label>
        <textarea
          id="cancel-reason"
          ref={reasonRef}
          className="input edit-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder="Customer returned all the books and took the money back"
        />

        <label className="checkbox cancel-req-check">
          <input
            type="checkbox"
            checked={handedBack}
            onChange={(e) => setHandedBack(e.target.checked)}
          />
          <span>I have handed {money(total)} back to the customer</span>
        </label>

        {error && (
          <p className="error error-block" role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={sending}>
            Go back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void send()}
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send to admin'}
          </button>
        </div>
      </div>
    </div>
  );
}
